import { useApolloClient, useMutation } from '@apollo/client';
import type { CreateUserProps, UseUserCreate } from '@openx/components-administration/lib/utils/context/UserHookTypes';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CreateUserUnderParentAccountDocument } from '../../../../graphql/mutation/users/CreateUserUnderParentAccount.generated';
import { CreateUserUnderRootAccountDocument } from '../../../../graphql/mutation/users/CreateUserUnderRootAccount.generated';
import { NestedErrorCode } from '../../../../modules/graphql/types';

export const useCreateUser: UseUserCreate = () => {
  const [createUserUnderParentAccountMutation] = useMutation(CreateUserUnderParentAccountDocument, {
    refetchQueries: ['GetUsersList'],
  });

  const [createUserUnderRootAccountMutation] = useMutation(CreateUserUnderRootAccountDocument, {
    refetchQueries: ['GetUsersList'],
  });

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const createUser = useCallback(
    async ({ type, user, shouldRedirect = true, onSuccess }: CreateUserProps) => {
      try {
        let isValidationErrorHandled = false;

        const errorCallback = (error: { code: NestedErrorCode; message?: string }) => {
          if (error.code === NestedErrorCode.VALIDATION_ERROR && error.message) {
            enqueueSnackbar(t(`User creation failed. ${error.message}`), { variant: 'error' });
            isValidationErrorHandled = true;
            return true;
          }
          return false;
        };

        // createUserUnderRootAccount doesn't accept parent_account_id as a variable
        const { parent_account_id, ...userWithoutParentAccountId } = user;

        const mutationCall =
          type === 'parent'
            ? createUserUnderParentAccountMutation({ variables: user })
            : createUserUnderRootAccountMutation({ variables: userWithoutParentAccountId });

        const result = await mutationCall.catch(error => {
          if (!errorCallback(error)) {
            throw error;
          }
        });

        if (isValidationErrorHandled) return;

        if (!result?.data) {
          throw new Error(t('Something went wrong creating user!'));
        }

        apolloClient.cache.evict({ fieldName: 'user', id: 'ROOT_QUERY' });
        // refreshes the paginated "Users With Access" list on the Organization Details page
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.ORGANIZATION_USERS] });
        enqueueSnackbar(t('User created successfully'), { variant: 'success' });

        onSuccess?.();

        if (shouldRedirect) navigate('/users');
      } catch (e) {
        enqueueSnackbar(t('An unexpected error occurred'), { variant: 'error' });
        // biome-ignore lint/suspicious/noConsole: logging info in console
        console.error(e);
      }
    },
    [
      createUserUnderParentAccountMutation,
      createUserUnderRootAccountMutation,
      enqueueSnackbar,
      t,
      navigate,
      apolloClient,
      queryClient,
    ]
  );

  return { createUser };
};
